import { readonly, ref } from 'vue'
import requestHandler from '@/composables/requestHandler'

const STORAGE_KEY = 'jobifull_feature_cohort'
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const flags = ref({})
const loaded = ref(false)
let cachedCohort = null
let loadPromise = null

function createUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`
}

function cohortKey() {
  if (cachedCohort) return cachedCohort

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored && UUID_PATTERN.test(stored)) {
      cachedCohort = stored
      return stored
    }
  } catch {
    // Browsers with restricted storage still receive a stable in-memory cohort.
  }

  cachedCohort = createUuid()
  try {
    sessionStorage.setItem(STORAGE_KEY, cachedCohort)
  } catch {
    // In-memory fallback is sufficient for this browser tab.
  }
  return cachedCohort
}

export function loadFeatureFlags(force = false) {
  if (loaded.value && !force) return Promise.resolve(flags.value)
  if (loadPromise) return loadPromise

  loadPromise = requestHandler.executeRequest('api/v1/feature-flags', {
    cohort_key: cohortKey(),
  }, 'get', { handleUnauthorized: false })
    .then(response => {
      flags.value = response.success && response.data?.data?.flags
        ? response.data.data.flags
        : {}
      loaded.value = true
      return flags.value
    })
    .catch(() => {
      flags.value = {}
      loaded.value = true
      return flags.value
    })
    .finally(() => {
      loadPromise = null
    })

  return loadPromise
}

export function isFeatureEnabled(key) {
  return flags.value[key] === true
}

export function useFeatureFlags() {
  return {
    flags: readonly(flags),
    loaded: readonly(loaded),
    loadFeatureFlags,
    isFeatureEnabled,
  }
}
